import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity, AlertTriangle, CheckCircle2, Eye, Loader2, Radar, Sparkles } from 'lucide-react';

import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/Spinner';
import { AnomalyList } from '@/features/quality/components/AnomalyList';
import { MetricChart } from '@/features/quality/components/MetricChart';
import { QualityScorecard } from '@/features/quality/components/QualityScorecard';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { KpiCard, formatNumber } from '@/features/dashboard/widgets';
import {
  useAnomalies,
  useDetect,
  useMetricSeries,
  useReviewAnomaly,
  useScorecard,
} from '@/features/quality/hooks';
import { METRIC_HELP, METRIC_LABELS, SENSITIVITY_LABELS, STATUS_LABELS } from '@/features/quality/labels';
import type { AnomalyStatus, QualityMetric, Sensitivity } from '@/features/quality/types';
import type { ApiError } from '@/lib/errors';

const METRICS: QualityMetric[] = [
  'null_rate',
  'distinct_count',
  'row_count',
  'pattern_conformance',
  'freshness_lag',
];
const SENSITIVITIES: Sensitivity[] = ['low', 'medium', 'high'];
const STATUS_FILTERS: Array<AnomalyStatus | 'all'> = ['open', 'acknowledged', 'resolved', 'all'];

const inputClass =
  'h-10 w-full rounded-lg border border-ink-200 dark:border-white/10 bg-white dark:bg-ink-900 px-3 text-sm text-ink-900 dark:text-ink-50 placeholder:text-ink-400 shadow-soft transition focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100';

export function QualityPage(): JSX.Element {
  const [assetFqn, setAssetFqn] = useState('');
  const [metric, setMetric] = useState<QualityMetric>('null_rate');
  const [sensitivity, setSensitivity] = useState<Sensitivity>('medium');
  const [target, setTarget] = useState<{ asset: string; metric: QualityMetric } | null>(null);
  const [statusFilter, setStatusFilter] = useState<AnomalyStatus | 'all'>('open');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const asset = searchParams.get('asset');
    if (asset === null || asset === '') {
      return;
    }
    const param = searchParams.get('metric');
    const m = (METRICS as string[]).includes(param ?? '') ? (param as QualityMetric) : 'row_count';
    setAssetFqn(asset);
    setMetric(m);
    setTarget({ asset, metric: m });
  }, [searchParams]);

  const detect = useDetect();
  const review = useReviewAnomaly();
  const series = useMetricSeries(
    target?.asset ?? '',
    target?.metric ?? 'null_rate',
    target !== null,
  );
  const anomalies = useAnomalies({ status: statusFilter === 'all' ? undefined : statusFilter });
  const allAnomalies = useAnomalies({});
  const scorecard = useScorecard();

  // Counts for the summary cards (always the full, unfiltered set).
  const counts = useMemo(() => {
    const all = allAnomalies.data ?? [];
    return {
      total: all.length,
      open: all.filter((a) => a.status === 'open').length,
      acknowledged: all.filter((a) => a.status === 'acknowledged').length,
      resolved: all.filter((a) => a.status === 'resolved').length,
    };
  }, [allAnomalies.data]);

  const anomalyEpochs = useMemo(
    () => new Set((detect.data ?? []).map((a) => new Date(a.ts).getTime())),
    [detect.data],
  );

  function handleDetect(event: FormEvent): void {
    event.preventDefault();
    const asset = assetFqn.trim();
    if (asset === '') {
      return;
    }
    setTarget({ asset, metric });
    detect.mutate({ asset_fqn: asset, metric, sensitivity });
  }

  return (
    <div className="space-y-7">
      <div>
        <div className="eyebrow eyebrow-dot mb-2.5">
          <span>MODULE</span>
        </div>
        <h1 className="h-display">Data Quality</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-500 dark:text-ink-400">
          We keep an eye on your data and flag anything that looks off — fields that suddenly go blank,
          big jumps or drops in records, or values that stop matching their usual pattern. Review what we
          find below.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          label="Needs Review"
          value={formatNumber(counts.open)}
          sub="issues waiting for you"
          icon={AlertTriangle}
          accent="crimson"
          alert={counts.open > 0}
        />
        <KpiCard
          label="In Progress"
          value={formatNumber(counts.acknowledged)}
          sub="seen, being looked into"
          icon={Eye}
          accent="amber"
        />
        <KpiCard
          label="Fixed"
          value={formatNumber(counts.resolved)}
          sub="issues resolved"
          icon={CheckCircle2}
          accent="brand"
        />
        <KpiCard
          label="Total Flagged"
          value={formatNumber(counts.total)}
          sub="found by automatic checks"
          icon={Sparkles}
          accent="violet"
        />
      </div>

      {scorecard.data && <QualityScorecard data={scorecard.data} />}

      <SectionShell
        icon={Radar}
        accent="sky"
        title="Check your data for problems"
        description="Pick a table or column and what to look at — we compare it against its own history and flag anything unusual."
      >
        <form onSubmit={handleDetect} className="grid grid-cols-1 gap-3 md:grid-cols-12">
          <label className="md:col-span-5 space-y-1.5">
            <span className="text-xs font-medium text-ink-700 dark:text-ink-200">Table or column</span>
            <input
              className={`${inputClass} font-mono`}
              placeholder="e.g. demo:orders  or  demo:orders.email"
              value={assetFqn}
              onChange={(e) => setAssetFqn(e.target.value)}
            />
          </label>
          <div className="md:col-span-3 space-y-1.5">
            <span className="text-xs font-medium text-ink-700 dark:text-ink-200">What to check</span>
            <SearchableSelect
              value={metric}
              onChange={(val) => setMetric(val as QualityMetric)}
              options={METRICS.map((m) => ({ label: METRIC_LABELS[m], value: m }))}
            />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <span className="text-xs font-medium text-ink-700 dark:text-ink-200">How strict</span>
            <SearchableSelect
              value={sensitivity}
              onChange={(val) => setSensitivity(val as Sensitivity)}
              options={SENSITIVITIES.map((s) => ({ label: SENSITIVITY_LABELS[s], value: s }))}
            />
          </div>
          <div className="md:col-span-2 flex items-end">
            <button
              type="submit"
              disabled={detect.isPending}
              className="btn-primary h-10 w-full px-3 text-xs"
            >
              {detect.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Check now'}
            </button>
          </div>
        </form>
        <p className="mt-2 text-xs text-ink-400 dark:text-ink-500">{METRIC_HELP[metric]}.</p>
        {detect.isError && (
          <p className="mt-2 font-mono text-xs text-accent-crimson">
            {(detect.error as ApiError).message}
          </p>
        )}

        {target !== null && (
          <div className="mt-5 space-y-3 border-t border-ink-100 dark:border-white/5 pt-5">
            {series.isPending ? (
              <Spinner />
            ) : series.isError ? (
              <ErrorState error={series.error as ApiError} />
            ) : (
              <MetricChart points={series.data} anomalyEpochs={anomalyEpochs} />
            )}
            {detect.data && (
              <p className="rounded-lg border border-brand-100 dark:border-brand-800/40 bg-brand-50/50 dark:bg-brand-500/10 px-3 py-2 text-sm text-ink-700 dark:text-ink-200">
                Found{' '}
                <span className="font-display font-semibold text-brand-700 dark:text-brand-300">
                  {detect.data.length}
                </span>{' '}
                possible issue{detect.data.length === 1 ? '' : 's'} in{' '}
                <span className="font-medium">{METRIC_LABELS[target.metric].toLowerCase()}</span>.
              </p>
            )}
          </div>
        )}
      </SectionShell>

      <SectionShell
        icon={Activity}
        accent="violet"
        title="Issues to review"
        description="Things we flagged. Mark each one as seen once you've looked, or fixed once it's sorted."
      >
        <div className="mb-4 inline-flex items-center gap-1 rounded-lg border border-ink-200/70 dark:border-white/10 bg-ink-50/60 dark:bg-white/[0.03] p-1">
          {STATUS_FILTERS.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(status)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                statusFilter === status
                  ? 'bg-white dark:bg-ink-900 text-ink-900 dark:text-ink-50 shadow-soft ring-1 ring-ink-200/70'
                  : 'text-ink-500 dark:text-ink-400 hover:text-ink-800 dark:hover:text-ink-200'
              }`}
            >
              {status === 'all' ? 'All' : STATUS_LABELS[status]}
            </button>
          ))}
        </div>

        {anomalies.isPending ? (
          <Spinner />
        ) : anomalies.isError ? (
          <ErrorState error={anomalies.error as ApiError} />
        ) : (
          <AnomalyList
            anomalies={anomalies.data}
            busyId={review.isPending ? review.variables.id : null}
            onAcknowledge={(id) => review.mutate({ id, action: 'acknowledge' })}
            onResolve={(id) => review.mutate({ id, action: 'resolve' })}
          />
        )}
      </SectionShell>
    </div>
  );
}

function SectionShell({
  icon: Icon,
  accent,
  title,
  description,
  children,
}: {
  icon: typeof Radar;
  accent: 'sky' | 'violet';
  title: string;
  description: string;
  children: React.ReactNode;
}): JSX.Element {
  const meta = {
    sky: { bg: 'bg-accent-sky/10', fg: 'text-accent-sky' },
    violet: { bg: 'bg-accent-violet/10', fg: 'text-accent-violet' },
  }[accent];
  return (
    <section className="rounded-xl border border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 shadow-soft animate-slide-up">
      <header className="flex items-start gap-3 border-b border-ink-100 dark:border-white/5 px-6 py-4">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${meta.bg} ${meta.fg}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <h3 className="h-section">{title}</h3>
          <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">{description}</p>
        </div>
      </header>
      <div className="p-6">{children}</div>
    </section>
  );
}
